// Enums
import { DETAILED_ASSET_ALLOCATION } from '@/enums/AssetAllocation';

// Types
import AppRegion from '@/types/app-region';
import LocalizedAllocationDetails from '@/types/localized-allocation-details';

// Utils
import { generateLocalizedString, LOCALE } from '@/utils/i18n';

export const isEquityAllocation = (name: string): boolean => {
  const lowerName = name.toLowerCase();
  return lowerName.includes(DETAILED_ASSET_ALLOCATION.EQ.toLowerCase());
};

export const isFixedIncomeAllocation = (name: string): boolean => {
  const lowerName = name.toLowerCase();
  return lowerName.includes(DETAILED_ASSET_ALLOCATION.FIXED_INCOME.toLowerCase());
};

export const isCashAllocation = (name: string): boolean => {
  const lowerName = name.toLowerCase();
  return lowerName.includes(DETAILED_ASSET_ALLOCATION.CASH.toLowerCase());
};

export const createOtherSubAllocation = (weight: number = 100): LocalizedAllocationDetails => ({
  name: generateLocalizedString(`Comparison.Table.AssetDetails.${DETAILED_ASSET_ALLOCATION.OTHER}`),
  weight,
});

const ensureSubAllocations = (allocation: LocalizedAllocationDetails): void => {
  if (!allocation.subAllocations?.length) {
    allocation.subAllocations = [createOtherSubAllocation()];
  }
};

const normalizeAllocationName = (allocation: LocalizedAllocationDetails): LocalizedAllocationDetails => {
  // If no name exists, return allocation as is
  if (!allocation.name) return allocation;

  // Extract the name once to avoid repeated access
  const localizedName = typeof allocation.name === 'string' ? allocation.name : allocation.name[LOCALE.EN_CA];

  if (!localizedName) return allocation;

  // Convert name to lowercase once
  const lowerName = localizedName.toLowerCase();

  // Create a standardized name based on allocation type
  let standardizedName: string | null = null;

  if (lowerName.includes(DETAILED_ASSET_ALLOCATION.EQ.toLowerCase())) {
    standardizedName = DETAILED_ASSET_ALLOCATION.EQ;
  } else if (lowerName.includes(DETAILED_ASSET_ALLOCATION.FIXED_INCOME.toLowerCase())) {
    standardizedName = DETAILED_ASSET_ALLOCATION.FIXED_INCOME;
  } else if (lowerName.includes(DETAILED_ASSET_ALLOCATION.CASH.toLowerCase())) {
    standardizedName = DETAILED_ASSET_ALLOCATION.CASH;
  }

  // If a standardized name was found, update the allocation
  if (standardizedName) {
    return {
      ...allocation,
      name: {
        [LOCALE.EN_CA]: standardizedName,
        [LOCALE.FR_CA]: standardizedName,
      },
    };
  }

  // If no match is found, return the original allocation
  return allocation;
};

export const processAllocationsForRegion = (allocations: LocalizedAllocationDetails[], appRegion: AppRegion): LocalizedAllocationDetails[] => {
  if (!allocations?.length || appRegion !== AppRegion.US) {
    return allocations;
  }

  return allocations.map(allocation => {
    const normalized = normalizeAllocationName(allocation);
    const localizedName = typeof normalized.name === 'string' ? normalized.name : normalized.name[LOCALE.EN_CA];

    if (isEquityAllocation(localizedName!) || isFixedIncomeAllocation(localizedName!) || isCashAllocation(localizedName!)) {
      ensureSubAllocations(normalized);
    }

    return normalized;
  });
};
